import { useEffect, useState } from "react";
import { Channel, Role, User } from "types";
import TopChannel from "../Text/Top/TopChannel";
import Icon from "@/icons";
import { activeSpeakerEmitter } from "@/eventEmitter";
import { useVoice } from "@/VoiceSystem";
import VoiceMember from "./Member";
import Tippy from "@tippyjs/react";

export default function VoiceChannelDisplay({ channel, user, me, socket, roles, members }: { channel: Channel; user: User; me: any; socket: any, roles: Role[], members: any }) {
    const voice = useVoice();
    const [currentSpeakers, setCurrentSpeakers] = useState<string[]>([]);
    const [focusedMember, setFocusedMember] = useState<string | null>(null);
    const [streamingUsers, setStreamingUsers] = useState<string[]>([]);
    useEffect(() => {
        const handleUpdateSpeakers = (attendeeIds: string[]) => {
            setCurrentSpeakers(attendeeIds);
        };

        activeSpeakerEmitter.on('updateSpeakers', handleUpdateSpeakers);

        const observer = {
            videoTileDidUpdate: (tileState: any) => {
                if (tileState.isContent) {
                    setStreamingUsers((prev) => [...prev, tileState.boundAttendeeId]);
                }
            },
        };

        voice?.meetingSessionRef?.current?.audioVideo?.addObserver(observer);

        return () => {
            activeSpeakerEmitter.off('updateSpeakers', handleUpdateSpeakers);
            voice?.meetingSessionRef?.current?.audioVideo?.removeObserver(observer);
        };
    }, [voice]);

    const handleFocus = (memberId: string) => {
        if (!channel.participants.find((participant: any) => participant.id === memberId)) return;
        if (focusedMember === memberId) return setFocusedMember(null);
        setFocusedMember(memberId);
    };

    const [isFullScreen, setIsFullScreen] = useState(false);

    useEffect(() => {
        const handleFullScreenChange = () => {
            setIsFullScreen(document.fullscreenElement !== null);
        };
        document.addEventListener("fullscreenchange", handleFullScreenChange);

        return () => {
            document.removeEventListener("fullscreenchange", handleFullScreenChange);
        };
    }, [isFullScreen]);

    return (
        <div className={`flex flex-col w-full items-center ${isFullScreen ? "fixed top-0 left-0 w-full h-full bg-brand-darkest" : "relative"}`} key={channel.id}>
            {!isFullScreen && <TopChannel channel={channel} />}
            <div className="flex flex-col relative items-center w-full h-full p-4 sm:p-10 md:p-24 justify-center select-none">

                {focusedMember && (
                    <>
                        <VoiceMember
                            participant={members?.data?.members.find((member: any) => member.id === focusedMember)}
                            isFocused={true}
                            isSpeaker={currentSpeakers.includes(channel.additionalData?.attendeeById[focusedMember] || '')}
                            handleFocus={handleFocus}
                            isMuted={channel.participants.find((participant: any) => participant.id === focusedMember)?.isMuted as boolean}
                            isDeafened={channel.participants.find((participant: any) => participant.id === focusedMember)?.isDeafened as boolean}
                            isStreaming={channel.participants.find((participant: any) => participant.id === focusedMember)?.isStreaming as boolean}
                            voice={voice}
                        />
                    </>
                )}

                <div className={`grid ${channel.participants.length === 1 ? "grid-cols-1" : `grid-cols-1 sm:grid-cols-2 md:grid-cols-${channel.participants.length <= 2 ? String(channel.participants.length) : "3"}`} gap-4 mt-2 justify-center h-96 items-center min-w-full align-middle`}>
                    {channel.participants
                        .filter((member: any) => member.id !== focusedMember)
                        .map((participant: any) => (
                            <VoiceMember
                                key={participant.id}
                                participant={members?.data?.members.find((member: any) => member.id === participant.id)}
                                isFocused={focusedMember === participant.id || channel.participants.length === 1}
                                isSpeaker={currentSpeakers.includes(channel.additionalData?.attendeeById[participant.id] || '')}
                                isMuted={participant.isMuted}
                                isDeafened={participant.isDeafened}
                                handleFocus={handleFocus}
                                isStreaming={participant.isStreaming}
                                voice={voice}
                            />
                        ))}
                </div>

                {voice?.channel?.id == channel.id && (
                    <div className="absolute bottom-24 sm:bottom-4 w-full flex justify-center">
                        <div className="flex items-center justify-center gap-4">
                            <button
                                className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-4 px-4 rounded-full"
                                onClick={() => {
                                    voice?.setVoiceData({
                                        isDeafened: !voice!.voiceData.isDeafened,
                                        isMuted: voice!.voiceData.isDeafened ? false : true
                                    });
                                }}
                            >
                                <Icon name={voice?.voiceData.isDeafened ? "voice-deafened" : "voice-nondeafened"} size="16" />
                            </button>

                            <button
                                className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-4 px-4 rounded-full"
                                onClick={() => {
                                    voice?.setVoiceData({
                                        isMuted: !voice!.voiceData.isMuted,
                                        isDeafened: voice!.voiceData.isMuted ? false : voice!.voiceData.isDeafened
                                    });
                                }}
                            >
                                <Icon name={voice?.voiceData.isMuted ? "voice-muted" : "voice-nonmuted"} size="16" />
                            </button>

                            <button
                                className={`text-white font-bold py-4 px-4 rounded-full ${channel.participants.find((participant: any) => participant.id === user.id)?.isStreaming ? "bg-gradient-to-r from-purple-500 to-purple-500" : "bg-gray-500 hover:bg-gray-600"}`}
                                onClick={() => {
                                    voice?.startScreenSharing();
                                }}
                            >
                                <Icon name={channel.participants.find((participant: any) => participant.id === user.id)?.isStreaming? "icon-share-screen-off" : "icon-share-screen"} size="16" />
                            </button>

                            <Tippy content="Leave Voice Channel">
                                <button
                                    className="bg-red-500 hover:bg-red-600 text-white font-bold py-4 px-4 rounded-full"
                                    onClick={() => {
                                        voice?.leaveVoiceChannel();
                                    }}
                                >
                                    <Icon name="call-end" size="16" />
                                </button>
                            </Tippy>
                        </div>
                    </div>
                ) || (
                        <div className="absolute bottom-24 sm:bottom-4 w-full flex justify-center">
                            <div className="flex items-center justify-center gap-4">
                                <Tippy content="Join Voice Channel">
                                    <button
                                        className="bg-green-500 hover:bg-green-600 text-white font-bold py-4 px-4 rounded-full"
                                        onClick={() => {
                                            voice?.joinVoiceChannel(channel, channel.teamId);
                                        }}
                                    >
                                        <Icon name="volume" size="16" />
                                    </button>
                                </Tippy>
                            </div>
                        </div>
                    )}
            </div>

            <div className="absolute z-50 flex gap-2 bottom-2 right-2 p-2 text-white font-bold">
                <button onClick={() => setIsFullScreen(
                    !isFullScreen
                )} className="text-white font-bold bg-black/50 p-2 rounded-full">
                    <Icon name="fullscreen" size="12" />
                </button>
            </div>
        </div>
    );
}
